import { type PatchedDeal, useApiDealsV1PartialUpdate, useApiDealsV1Retrieve } from 'api';
import { translate } from 'i18n';
import { addError } from 'monitor';
import { useMemo } from 'react';
import { DetailsList, Link, Skeleton, Stack, Typography } from 'ui';

import { DealInspectionsList } from '../../../inspection';
import { RiskSelect } from '../../../risk';

export const DealDetails = ({ dealId }: { dealId: number }) => {
  const { data: deal, isFetching } = useApiDealsV1Retrieve(dealId);
  const { mutate: updateDeal } = useApiDealsV1PartialUpdate();

  const detailsFields = useMemo(
    () => [
      {
        title: translate('backlog.dealDetails.fields.loads', { namespace: 'features' }),
        value: deal?.loads_count,
      },
      {
        title: translate('backlog.dealDetails.fields.material', { namespace: 'features' }),
        value: deal?.material_full_description,
      },
      {
        title: translate('backlog.dealDetails.fields.producer', { namespace: 'features' }),
        value: deal?.purchase_order.producer.name,
      },
      {
        title: translate('backlog.dealDetails.fields.endProcessor', { namespace: 'features' }),
        value: deal?.sales_order.end_processor.name,
      },
      {
        title: translate('backlog.dealDetails.fields.loadingSite', { namespace: 'features' }),
        value: deal?.purchase_order.collection_address.full_address,
      },
      {
        title: translate('backlog.dealDetails.fields.destination', { namespace: 'features' }),
        value: deal?.sales_order.final_destination_address?.full_address,
      },
      {
        title: translate('backlog.dealDetails.fields.risk', { namespace: 'features' }),
        value: (
          <RiskSelect
            currentValue={deal?.risk_score || null}
            onChange={(riskScore) => {
              if (!deal?.id) {
                addError(Error('Deal id missing during risk score update'), deal);
                return;
              }
              updateDeal({
                data: {
                  risk_score: riskScore,
                } as PatchedDeal,
                id: deal.id,
              });
            }}
          />
        ),
      },
      {
        title: translate('backlog.dealDetails.fields.inspections', { namespace: 'features' }),
        value: deal && <DealInspectionsList dealId={deal.id} />,
      },
    ],
    [deal, updateDeal]
  );

  if (isFetching || !deal) {
    return (
      <>
        {Array.from({ length: detailsFields.length }).map((_, index) => (
          <Skeleton
            key={index}
            sx={{
              height: 60,
            }}
          />
        ))}
      </>
    );
  }

  const slug = deal.purchase_order.slug;
  return (
    <div>
      <Stack
        direction="row"
        sx={{
          alignItems: 'baseline',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Typography variant="h5" component={Link} href={deal.gc_url} underline="none">
          {translate('backlog.dealDetails.header.deal', {
            namespace: 'features',
            values: { dealId },
          })}
        </Typography>
        {slug !== null && (
          <Typography
            data-testid="deal-slug"
            variant="body1"
            component={Link}
            href={deal.purchase_order.gc_url}
            underline="none"
          >
            {slug}
          </Typography>
        )}
      </Stack>
      <DetailsList details={detailsFields} />
    </div>
  );
};
